import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { capitalize } from '@mui/material/utils';
import type { PieValueType } from '@mui/x-charts/models';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import { Match, Show, Switch } from '@side/pantry';
import { useState } from 'react';
import type { InteractionType } from '@/common/types/interaction';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { PieArc } from './PieArc';
import { PieChartCenterLabel } from './PieChartCenterLabel';
import { PieChartTooltip } from './PieChartTooltip';
import { useRouteToTLV } from './useRouteToTLV';

const MOBILE_DIAMETER = 160;
const NONMOBILE_DIAMETER = 188;

type PieChartProps = {
  data: PieValueType[];
  interactionType: InteractionType;
  isLoading: boolean;
  isMobile: boolean;
};

export function PieChart(props: PieChartProps) {
  const {
    data = [],
    isLoading = false,
    isMobile = false,
    interactionType,
  } = props;
  const [isFullLegendShown, setIsFullLegendShown] = useState(false);
  const trackEvent = useTrackHomePageAnalytics();
  const { routeToTLVPageWithFiltersApplied, clearLastClicked } = useRouteToTLV(
    interactionType,
    isMobile,
  );

  const statuses = isLoading
    ? new Array<PieValueType>(3).fill({} as PieValueType)
    : data;
  const pluralInteractionType = `${interactionType}s`;
  const diameter = isMobile ? MOBILE_DIAMETER : NONMOBILE_DIAMETER;
  const countOfHiddenLegendItems = data.length - 3;
  const aggregateCountOfInteractions = data?.reduce(
    (sum, { value = 0 }) => sum + value,
    0,
  );

  return (
    <ClickAwayListener onClickAway={clearLastClicked}>
      <Box
        data-testid="PieChart"
        sx={{
          minWidth: { xs: 343, md: 228 },
          maxWidth: { xs: 343, md: 228 },
        }}
      >
        <Typography sx={{ paddingBottom: 3 }} variant="h4">
          {capitalize(pluralInteractionType)}
        </Typography>

        <Stack
          sx={{
            flexDirection: { xs: 'row', md: 'column' },
            gap: { xs: 3 },
            justifyContent: { xs: 'space-evenly', md: undefined },
          }}
        >
          <Switch>
            <Match when={isLoading}>
              <Box
                sx={{
                  height: diameter,
                  margin: { xs: 0, md: 'auto' },
                  width: diameter,
                }}
              >
                <Skeleton
                  data-testid="PieChart__Skeleton"
                  height={diameter}
                  sx={{
                    margin: { xs: 0, md: 'auto' },
                  }}
                  variant="circular"
                  width={diameter}
                />
              </Box>
            </Match>

            <Match when={!aggregateCountOfInteractions && !isLoading}>
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: (theme) => theme.palette.grey[100],
                  borderRadius: 0.5,
                  display: 'flex',
                  height: { xs: 130, md: 188 },
                  justifyContent: 'center',
                  margin: { xs: 0, md: 'auto' },
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                    padding: 4,
                    textAlign: 'center',
                    textWrap: 'wrap',
                  }}
                  variant="body3"
                >
                  {`No ${pluralInteractionType} found that match the criteria`}
                </Typography>
              </Box>
            </Match>

            <Match when={!isLoading && aggregateCountOfInteractions}>
              <Box
                sx={{
                  height: diameter,
                  margin: { xs: 0, md: 'auto' },
                  width: diameter,
                }}
              >
                <MuiPieChart
                  height={diameter}
                  margin={{
                    bottom: 0,
                    left: 0,
                    right: 0,
                    top: 0,
                  }}
                  onItemClick={routeToTLVPageWithFiltersApplied}
                  series={[
                    {
                      data,
                      faded: {
                        color: '#000000', // opacity of 0.3 is applied by default
                      },
                      highlightScope: { fade: 'global', highlight: 'item' },
                      id: interactionType,
                      innerRadius: isMobile ? 60 : 70,
                      paddingAngle: 0.6,
                    },
                  ]}
                  hideLegend
                  slots={{
                    tooltip: PieChartTooltip,
                    pieArc: PieArc,
                  }}
                  width={diameter}
                >
                  <PieChartCenterLabel>
                    {aggregateCountOfInteractions > 0
                      ? aggregateCountOfInteractions
                      : ''}
                  </PieChartCenterLabel>
                </MuiPieChart>
              </Box>
            </Match>
          </Switch>

          <Show when={aggregateCountOfInteractions || isLoading}>
            <Stack
              component="ul"
              data-testid="PieChart__Legend"
              sx={{
                marginBlockEnd: 0,
                marginBlockStart: 0,
                marginTop: { xs: 3, md: 0 },
                paddingInlineStart: 0,
              }}
            >
              <Collapse
                collapsedSize={96}
                in={isFullLegendShown}
                orientation="vertical"
              >
                {statuses.map((status, index) => {
                  const {
                    color = '',
                    id = index,
                    label = '',
                    value = '',
                  } = status;

                  const isHidden = !isFullLegendShown && index > 2;

                  return (
                    <Stack
                      component="li"
                      data-testid="PieChart__LegendItem"
                      hidden={isHidden}
                      key={id}
                      sx={{
                        alignItems: 'center',
                        flexDirection: 'row',
                        height: 32,
                        justifyContent: 'space-between',
                        pl: { xs: 0, md: 1 },
                        pr: { xs: 0, md: 4 },
                        width: '100%',
                      }}
                    >
                      <Stack
                        sx={{
                          alignItems: 'center',
                          flexDirection: 'row',
                          gap: 1.5,
                          margin: 'auto',
                          minWidth: 128,
                          width: '100%',
                        }}
                      >
                        <Show
                          fallback={
                            <Skeleton
                              height={12}
                              variant="circular"
                              width={12}
                            />
                          }
                          when={!isLoading}
                        >
                          <Box
                            sx={{
                              backgroundColor: color,
                              borderRadius: '50%',
                              height: 12,
                              width: 12,
                            }}
                          />
                        </Show>

                        <Show
                          fallback={<Skeleton width={100} />}
                          when={!isLoading}
                        >
                          <Typography
                            noWrap
                            sx={{
                              color: 'text.secondary',
                              maxWidth: { xs: 100, md: 'unset' },
                            }}
                            variant="body2"
                          >
                            {typeof label === 'string'
                              ? label
                              : label('legend')}
                          </Typography>
                        </Show>
                      </Stack>

                      <Show
                        fallback={<Skeleton width={18} />}
                        when={!isLoading}
                      >
                        <Typography sx={{ fontWeight: 500 }} variant="body2">
                          {value}
                        </Typography>
                      </Show>
                    </Stack>
                  );
                })}
              </Collapse>

              <Show
                fallback={
                  <Skeleton
                    sx={{
                      marginBottom: { xs: 2, md: 0.5 },
                      marginLeft: { xs: 3, md: 4 },
                      marginTop: { xs: 1.5, md: 1.5, lg: 1 },
                    }}
                    width={70}
                  />
                }
                when={!isLoading}
              >
                <Show when={countOfHiddenLegendItems > 0}>
                  <Button
                    onClick={() => {
                      if (isFullLegendShown) {
                        trackEvent(
                          TrackHomePageEvent.FactsAndFiguresSegmentShowLessClick,
                          {
                            interactionType,
                          },
                        );
                      } else {
                        trackEvent(
                          TrackHomePageEvent.FactsAndFiguresSegmentShowMoreClick,
                          {
                            interactionType,
                          },
                        );
                      }

                      setIsFullLegendShown((prevState) => !prevState);
                    }}
                    sx={{
                      fontSize: 11,
                      fontWeight: 500,
                      marginTop: { xs: 0.5, md: 0 },
                      justifyContent: 'flex-start',
                      marginLeft: { xs: 2, md: 3 },
                    }}
                    variant="text"
                  >
                    {isFullLegendShown
                      ? 'Show less'
                      : `Show ${countOfHiddenLegendItems} more`}
                  </Button>
                </Show>
              </Show>
            </Stack>
          </Show>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
}
